import { useEffect, useRef } from 'react';
import { Control, FieldValues, Path, useController } from 'react-hook-form';

// Threshold-aware HSC.
//   diluted% >= threshold     ⇒ aboveThreshold = true
//   diluted% <  threshold     ⇒ aboveThreshold = false
//
// HSC auto-toggle (edge-triggered):
//   below → above    setHsc(true)
//   above → below    setHsc(false)
//   same side        no-op (preserves manual override)
//   first render     stamp the side, don't touch HSC (Edit forms keep
//                    their stored value)
//
// Returns aboveThreshold so callers can run their own auto-fills (e.g.
// fill the controlType picker only when both HSC=true and aboveThreshold).
export function useHscThresholdAutoToggle<TForm extends FieldValues>({
  control,
  uboThresholdPercent,
  getDilutedPercent = (p) => p,
  stockClassId,
}: {
  control: Control<TForm>;
  uboThresholdPercent: number;
  getDilutedPercent?: (
    perClassPercent: number,
    stockClassId: string | null,
  ) => number | null;
  // Resolved id — null for a staged class that doesn't exist yet, which is what
  // `getDilutedPercent` keys its staged-authorized-count branch on. Not read off
  // form state: that field carries STAGED_STOCK_CLASS_ID while a draft is staged.
  stockClassId: string | null;
}): { aboveThreshold: boolean } {
  const ownershipPercentField = useController({
    control,
    name: 'ownershipPercent' as Path<TForm>,
  }).field;
  const hscField = useController({
    control,
    name: 'hasSignificantControl' as Path<TForm>,
  }).field;

  const lastAboveThresholdRef = useRef<boolean | undefined>(undefined);
  const hasSeenInitialRef = useRef(false);

  const ownershipPercent = ownershipPercentField.value as number | undefined;
  const setHsc = hscField.onChange;

  const dilutedPercent =
    typeof ownershipPercent === 'number'
      ? getDilutedPercent(ownershipPercent, stockClassId)
      : null;
  // >= so the threshold itself qualifies (25% on a 25% jurisdiction).
  const aboveThreshold =
    dilutedPercent !== null && dilutedPercent >= uboThresholdPercent;

  useEffect(() => {
    // No % typed yet — nothing to evaluate.
    if (dilutedPercent === null) return;

    // First evaluation: stamp the side, leave HSC untouched.
    if (!hasSeenInitialRef.current) {
      hasSeenInitialRef.current = true;
      lastAboveThresholdRef.current = aboveThreshold;
      return;
    }
    // Edge-trigger: only fire on actual side changes.
    const wasAbove = lastAboveThresholdRef.current ?? false;
    if (aboveThreshold && !wasAbove) setHsc(true);
    else if (!aboveThreshold && wasAbove) setHsc(false);
    lastAboveThresholdRef.current = aboveThreshold;
  }, [aboveThreshold, dilutedPercent, setHsc]);

  return { aboveThreshold };
}
